import { SUPPORTED_LANGUAGES } from '@/utils/constants';
import React, { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';

export const useTTS = ({
  socket,
  setIsAISpeaking,
  onIntroComplete,
  introAudioCompleteRef,
  isIntroCompletedRef,
}: {
  socket: WebSocket;
  setIsAISpeaking: (isAISpeaking: boolean) => void;
  onIntroComplete: () => void;
  introAudioCompleteRef: React.MutableRefObject<boolean>;
  isIntroCompletedRef: React.MutableRefObject<boolean>;
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isPlayingRef = useRef(false);

  useEffect(() => {
    if (!socket) {
      console.error('Invalid socket object provided to useTTS');
      return;
    }

    // Function to handle WebSocket messages for TTS
    const handleMessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);

        if (data.audioContent) {
          // Convert base64 to blob
          const audioBlob = base64ToBlob(data.audioContent, 'audio/mp3');
          const url = URL.createObjectURL(audioBlob);

          // Create and play audio
          const audio = new Audio(url);
          audioRef.current = audio;

          audio.play();

          // When audio playback ends
          audio.onended = () => {
            console.log('Intro audio playback finished.');
            setTimeout(() => {
              setIsAISpeaking(false);

              // Mark intro as completed
              introAudioCompleteRef.current = true;
              isIntroCompletedRef.current = true;

              // Transition to main conversation
              onIntroComplete();
            }, 500);
          };
        }

        if (data.error) {
          console.error('TTS Error:', data.error);
          setIsAISpeaking(false);
          toast.error('Failed to play introduction');
          onIntroComplete();
        }
      } catch (error) {
        console.error('Error processing TTS message:', error);
        setIsAISpeaking(false);
        toast.error('Failed to process audio data');
        onIntroComplete();
      }
    };

    // Function to handle WebSocket errors
    const handleError = (error: Event) => {
      console.error('TTS WebSocket error:', error);
      setIsAISpeaking(false);
      toast.error('Failed to connect to TTS service');
      onIntroComplete();
    };

    // Register event handlers
    socket.addEventListener('message', handleMessage);
    socket.addEventListener('error', handleError);

    return () => {
      // Clean up event listeners
      socket.removeEventListener('message', handleMessage);
      socket.removeEventListener('error', handleError);

      // Stop audio playback
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [socket, setIsAISpeaking, onIntroComplete]);

  // Helper function to convert base64 to Blob
  const base64ToBlob = (base64: string, mime: string) => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mime });
  };

  const playIntro = (sessionId: string, text: string, language = 'en') => {
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      console.error('Socket is not open');
      toast.error('TTS service not connected');
      onIntroComplete();
      return;
    }

    if (isPlayingRef.current) return;
    isPlayingRef.current = true;

    // Format language code based on session language
    const currentLanguage: any = SUPPORTED_LANGUAGES.find((language: any) => language.code === language);
    const languageCode = currentLanguage ? currentLanguage.TTS : 'en-US';

    // Send message to TTS WebSocket
    socket.send(
      JSON.stringify({
        text,
        languageCode,
      }),
    );

    console.log('Sent intro text to TTS service:', text);
  };

  return { playIntro };
};
